/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import { AppProps as ApplicationProps } from 'next/app';
import Script from 'next/script';
import { withTranslation } from 'react-i18next';
import '../index.css';
import 'normalize.css';
import AxonScripts from 'core/analytics/ui/AxonScripts';
import BingAdsScripts from 'core/analytics/ui/BingAdsScripts';
import FacebookScripts from 'core/analytics/ui/FacebookScripts';
import SnapchatScripts from 'core/analytics/ui/SnapchatScripts';
import TaboolaScripts from 'core/analytics/ui/TaboolaScripts';
import TikTokScripts from 'core/analytics/ui/TikTokScripts';
import sprite from 'core/common/assets/images/sprites/sprite.svg';
import { MetaData } from 'core/common/components/MetaData';
import { CustomAppEvents } from 'core/common/entities';
import { getRobotsContentByEnvironment } from 'core/common/utils/getRobotsContentByEnvironment';
import { prepareHtml } from 'core/common/utils/prepareHtml';
import { FunnelAnalytics } from 'core/funnel/entities';
import { StyleSheetManager } from 'core/localization/ui';
import { useAnalyticsIds } from 'core/scripts/hooks';
import App from 'src/app';
import { config } from 'src/config';

const Application = ({
  Component,
  pageProps,
}: ApplicationProps & { analytics: FunnelAnalytics; currentFunnelName: string }) => {
  const analyticsPixelIds = useAnalyticsIds({ funnelAnalytics: pageProps.analytics });

  const isFunnelWithMinimalAnalytics = config.funnelsWithMinimalAnalytics.includes(
    pageProps.currentFunnelName,
  );
  const isFunnelWithoutClarity = config.funnelsWithoutClarity.includes(pageProps.currentFunnelName);
  const robotsContent = getRobotsContentByEnvironment(config);

  return (
    <>
      <MetaData title={pageProps.currentFunnelName} robots={robotsContent} spriteHref={sprite} />

      <App
        analyticsPixelIds={analyticsPixelIds}
        screenId={pageProps.screenId}
        funnelId={pageProps.funnelId}
        funnelConfig={pageProps.funnelConfig ?? null}
        isDraftRedesignFlow={pageProps.isDraftRedesignFlow}
        features={pageProps.features}
      >
        <StyleSheetManager>
          <Component {...pageProps} />
        </StyleSheetManager>
      </App>

      {/* _AutofillCallbackHandler for in-app browsers */}
      <Script
        strategy="beforeInteractive"
        id="autofill-callback-handler"
        type="text/javascript"
        dangerouslySetInnerHTML={prepareHtml(
          'window._AutofillCallbackHandler = window._AutofillCallbackHandler || function () {};',
        )}
      />
      {/* End _AutofillCallbackHandler for in-app browsers */}

      {/* OneTrust Cookies consent */}
      <Script
        id="one-trust-cookie-consent"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        type="text/javascript"
        data-domain-script={config.oneTrust.key}
      />

      <Script
        id="one-trust-cookie-consent-init"
        type="text/javascript"
        dangerouslySetInnerHTML={prepareHtml(`function OptanonWrapper() {
             try {
               const event = new Event('${CustomAppEvents.ONE_TRUST_LOADED}');
               document.dispatchEvent(event);
             } catch (error) {
               console.error('Error dispatching OneTrust loaded event', error);
             }
        }`)}
      />
      {/* End OneTrust Cookies consent */}

      <FacebookScripts ids={analyticsPixelIds.facebook.nonConditional} />

      <SnapchatScripts ids={analyticsPixelIds.snapchat.nonConditional} />

      {isFunnelWithMinimalAnalytics ? null : (
        <>
          <AxonScripts ids={analyticsPixelIds.axon.nonConditional} />

          <TikTokScripts ids={analyticsPixelIds.tikTok.nonConditional} />

          <BingAdsScripts ids={analyticsPixelIds.bingAds.nonConditional} />

          <TaboolaScripts ids={analyticsPixelIds.taboola.nonConditional} />

          <Script
            id="page-view-gtm"
            dangerouslySetInnerHTML={prepareHtml(`window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
            originalLocation: document.location.protocol + '//' +
            document.location.hostname +
            document.location.pathname +
            document.location.search
            })`)}
          />

          {/* Google Tag Manager */}
          <Script
            id="gtm"
            dangerouslySetInnerHTML={prepareHtml(`(function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
            var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-WFRV4R6');`)}
          />
          {/* End Google Tag Manager */}

          {/* Microsoft Clarity - HeatMap and SessionRecording */}
          <Script
            id="clarityHeatmap"
            strategy="beforeInteractive"
            onReady={() => {
              try {
                const event = new Event(CustomAppEvents.CLARITY_LOADED);
                document.dispatchEvent(event);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error dispatching Clarity loaded event', error);
              }
            }}
            dangerouslySetInnerHTML={{
              __html: config.clarityId
                ? `(function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            t.id="clarityScript";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${config.clarityId}");`
                : '',
            }}
          />
          {/* End Microsoft Clarity - HeatMap and SessionRecording */}
        </>
      )}

      {/* Solid Payment Form */}
      <Script defer src="https://cdn.solidgate.com/js/solid-form.js" />
      {/* End Solid Payment Form */}

      {isFunnelWithoutClarity ? null : (
        <>
          {/* Microsoft Clarity - HeatMap and SessionRecording */}
          <Script
            id="clarityHeatmap"
            strategy="beforeInteractive"
            onReady={() => {
              try {
                const event = new Event(CustomAppEvents.CLARITY_LOADED);
                document.dispatchEvent(event);
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error dispatching Clarity loaded event', error);
              }
            }}
            dangerouslySetInnerHTML={{
              __html: config.clarityId
                ? `(function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            t.id="clarityScript";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${config.clarityId}");`
                : '',
            }}
          />
          {/* End Microsoft Clarity - HeatMap and SessionRecording */}
        </>
      )}

      <noscript>
        <iframe
          title="gtm"
          src="https://www.googletagmanager.com/ns.html?id=GTM-WFRV4R6"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
      <noscript>You need to enable JavaScript to run this app.</noscript>
    </>
  );
};

export default withTranslation()(Application);
